/**
 * sl-728o.2.1: TutorialRenderer Component
 *
 * Renders an interactive tutorial with spotlight highlighting and tooltip navigation.
 * Supports two modes:
 * - Connected: Reads from TutorialRegistry (for registry-driven tutorials)
 * - Standalone: Manages its own state (for legacy coachmark compatibility)
 */

import {
  StreamLainCoachmarkCheckboxRow,
  type StreamLainCoachmarkNotchPlacement,
  StreamLainCoachmarkShell,
} from '@affine/core/components/coachmark';
import { useI18n } from '@affine/i18n';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { ArrowLeftSmallIcon, ArrowRightSmallIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import type { CSSProperties, FC, ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { DEMO_TOUR_SESSION_ID } from '../constants';
import { useTutorialTrigger } from '../hooks/use-tutorial-trigger';
import { TutorialRegistry } from '../services/tutorial-registry';
import type { TutorialStep } from '../types';
import { CanvasNavigationDemo } from './canvas-navigation-demo';
import { TutorialComparisonSceneView } from './note-click-scenes';
import * as styles from './tutorial-renderer.css';
import { TutorialSuggestionIllustrationView } from './tutorial-suggestion-illustration';

// --- Tooltip Positioning ---

const TOOLTIP_WIDTH = 300; // matches CSS .tooltip width
const TOOLTIP_HEIGHT_EST = 220; // estimated max height (content varies)
const MOBILE_BREAKPOINT = 900;
const MOBILE_TOOLTIP_HEIGHT_EST = 320;
const MARGIN = 16; // minimum distance from viewport edge
const GAP = 12; // gap between target and tooltip
const NOTCH_SIZE = 12;
const NOTCH_EDGE_PADDING = 16;

export type TutorialTooltipPlacement = 'right' | 'left' | 'bottom' | 'top';

export interface TutorialRect {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
}

export interface TutorialTooltipGeometry {
  largeTarget: boolean;
  notch: StreamLainCoachmarkNotchPlacement;
  placement: TutorialTooltipPlacement;
  style: CSSProperties;
}

interface TutorialTooltipGeometryOptions {
  allowNotch?: boolean;
  centerOnTarget?: boolean;
  forcePreferredPosition?: boolean;
  preferredPosition?: TutorialStep['position'];
  targetRect: TutorialRect;
  tooltipHeight?: number;
  tooltipWidth?: number;
  viewportHeight?: number;
  viewportWidth?: number;
}

function isCompactViewport(width: number): boolean {
  return width <= MOBILE_BREAKPOINT;
}

function usesTouchTutorialControls(width: number): boolean {
  return (
    width <= 560 ||
    window.matchMedia?.('(hover: none) and (pointer: coarse)').matches === true
  );
}

export function measureTutorialTooltip(element: HTMLElement): {
  height: number;
  width: number;
} {
  const rect = element.getBoundingClientRect();
  return {
    height: element.offsetHeight || rect.height,
    width: element.offsetWidth || rect.width,
  };
}

export function shouldUseDemoTourDisclosureSurface(
  sessionId?: string,
  surface?: TutorialStep['surface']
): boolean {
  return surface === 'disclosure' || sessionId === DEMO_TOUR_SESSION_ID;
}

export function isLargeTutorialTargetRect(
  targetRect: TutorialRect,
  viewportWidth: number,
  viewportHeight: number
): boolean {
  return (
    targetRect.height > viewportHeight * 0.42 ||
    targetRect.width > viewportWidth * 0.82
  );
}

function placementToNotch(
  placement: TutorialTooltipPlacement
): StreamLainCoachmarkNotchPlacement {
  const notchByPlacement: Record<
    TutorialTooltipPlacement,
    StreamLainCoachmarkNotchPlacement
  > = {
    bottom: 'top',
    left: 'right',
    right: 'left',
    top: 'bottom',
  };

  return notchByPlacement[placement];
}

function clampNotchOffset(
  targetCenter: number,
  panelStart: number,
  panelSize: number
): number {
  const rawOffset = targetCenter - panelStart - NOTCH_SIZE / 2;
  const maxOffset = Math.max(
    NOTCH_EDGE_PADDING,
    panelSize - NOTCH_EDGE_PADDING - NOTCH_SIZE
  );

  return Math.max(NOTCH_EDGE_PADDING, Math.min(rawOffset, maxOffset));
}

function withNotchPositionVars({
  notch,
  style,
  targetRect,
  tooltipHeight,
  tooltipWidth,
}: {
  notch: StreamLainCoachmarkNotchPlacement;
  style: CSSProperties;
  targetRect: TutorialRect;
  tooltipHeight: number;
  tooltipWidth: number;
}): CSSProperties {
  if (notch === 'none') {
    return style;
  }

  const nextStyle = { ...style } as CSSProperties & Record<string, string>;
  const panelLeft = typeof style.left === 'number' ? style.left : MARGIN;
  const panelTop = typeof style.top === 'number' ? style.top : MARGIN;
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  if (notch === 'top' || notch === 'bottom') {
    const inlineOffset = clampNotchOffset(
      targetCenterX,
      panelLeft,
      tooltipWidth
    );
    const transformOrigin =
      notch === 'top'
        ? `${inlineOffset + NOTCH_SIZE / 2}px top`
        : `${inlineOffset + NOTCH_SIZE / 2}px bottom`;
    nextStyle['--streamlain-coachmark-notch-inline'] = `${inlineOffset}px`;
    nextStyle['--streamlain-coachmark-transform-origin'] = transformOrigin;
    nextStyle['--streamlain-disclosure-overlay-transform-origin'] =
      transformOrigin;
  } else {
    const blockOffset = clampNotchOffset(
      targetCenterY,
      panelTop,
      tooltipHeight
    );
    const transformOrigin =
      notch === 'left'
        ? `left ${blockOffset + NOTCH_SIZE / 2}px`
        : `right ${blockOffset + NOTCH_SIZE / 2}px`;
    nextStyle['--streamlain-coachmark-notch-block'] = `${blockOffset}px`;
    nextStyle['--streamlain-coachmark-transform-origin'] = transformOrigin;
    nextStyle['--streamlain-disclosure-overlay-transform-origin'] =
      transformOrigin;
  }

  return nextStyle;
}

export function resolveTutorialTooltipGeometry({
  allowNotch = true,
  centerOnTarget = false,
  forcePreferredPosition = false,
  preferredPosition,
  targetRect,
  tooltipHeight: measuredTooltipHeight,
  tooltipWidth: measuredTooltipWidth,
  viewportHeight = window.innerHeight,
  viewportWidth = window.innerWidth,
}: TutorialTooltipGeometryOptions): TutorialTooltipGeometry {
  const vw = viewportWidth;
  const vh = viewportHeight;
  const isMobile = isCompactViewport(vw);
  const tooltipWidth = Math.min(
    measuredTooltipWidth ?? TOOLTIP_WIDTH,
    vw - MARGIN * 2
  );
  const tooltipHeight = Math.min(
    measuredTooltipHeight ??
      (isMobile ? MOBILE_TOOLTIP_HEIGHT_EST : TOOLTIP_HEIGHT_EST),
    vh - MARGIN * 2
  );
  const largeTarget = isLargeTutorialTargetRect(targetRect, vw, vh);

  if (isMobile) {
    const targetMidY = targetRect.top + targetRect.height / 2;
    const bottomSpace = vh - targetRect.bottom - MARGIN;
    const preferTop =
      largeTarget ||
      targetMidY > vh * 0.48 ||
      bottomSpace < tooltipHeight + GAP;
    const top = preferTop
      ? MARGIN
      : Math.min(targetRect.bottom + GAP, vh - tooltipHeight - MARGIN);
    const placement: TutorialTooltipPlacement = preferTop ? 'top' : 'bottom';
    const notch = allowNotch ? placementToNotch(placement) : 'none';
    const style: CSSProperties = {
      position: 'fixed',
      left: Math.max(
        MARGIN,
        Math.min(
          centerOnTarget
            ? targetRect.left + targetRect.width / 2 - tooltipWidth / 2
            : (vw - tooltipWidth) / 2,
          vw - tooltipWidth - MARGIN
        )
      ),
      top: Math.max(MARGIN, Math.min(top, vh - tooltipHeight - MARGIN)),
    };

    return {
      largeTarget,
      notch,
      placement,
      style: withNotchPositionVars({
        notch,
        style,
        targetRect,
        tooltipHeight,
        tooltipWidth,
      }),
    };
  }

  const fits: Record<TutorialTooltipPlacement, boolean> = {
    bottom: targetRect.bottom + GAP + tooltipHeight + MARGIN < vh,
    top: targetRect.top - GAP - tooltipHeight > MARGIN,
    right: targetRect.right + GAP + tooltipWidth + MARGIN < vw,
    left: targetRect.left - GAP - tooltipWidth > MARGIN,
  };

  const placements: Record<
    TutorialTooltipPlacement,
    { left: number; top: number }
  > = {
    bottom: {
      left: centerOnTarget
        ? targetRect.left + targetRect.width / 2 - tooltipWidth / 2
        : targetRect.left,
      top: targetRect.bottom + GAP,
    },
    top: {
      left: centerOnTarget
        ? targetRect.left + targetRect.width / 2 - tooltipWidth / 2
        : targetRect.left,
      top: targetRect.top - tooltipHeight - GAP,
    },
    right: {
      left: targetRect.right + GAP,
      top: centerOnTarget
        ? targetRect.top + targetRect.height / 2 - tooltipHeight / 2
        : targetRect.top,
    },
    left: {
      left: targetRect.left - tooltipWidth - GAP,
      top: centerOnTarget
        ? targetRect.top + targetRect.height / 2 - tooltipHeight / 2
        : targetRect.top,
    },
  };

  // Determine which placement to use
  const fallbackOrder: TutorialTooltipPlacement[] = centerOnTarget
    ? ['bottom', 'top', 'right', 'left']
    : ['bottom', 'right', 'top', 'left'];
  let chosen: TutorialTooltipPlacement;

  if (
    forcePreferredPosition &&
    preferredPosition &&
    preferredPosition !== 'auto'
  ) {
    chosen = preferredPosition;
  } else if (
    preferredPosition &&
    preferredPosition !== 'auto' &&
    fits[preferredPosition]
  ) {
    chosen = preferredPosition;
  } else {
    chosen = fallbackOrder.find(p => fits[p]) ?? 'bottom';
  }

  const pos = placements[chosen];

  // Clamp within viewport
  const clampedLeft = Math.max(
    MARGIN,
    Math.min(pos.left, vw - tooltipWidth - MARGIN)
  );
  const clampedTop = Math.max(
    MARGIN,
    Math.min(pos.top, vh - tooltipHeight - MARGIN)
  );
  const notch = allowNotch ? placementToNotch(chosen) : 'none';
  const style: CSSProperties = {
    position: 'fixed',
    left: clampedLeft,
    top: clampedTop,
  };

  return {
    largeTarget,
    notch,
    placement: chosen,
    style: withNotchPositionVars({
      notch,
      style,
      targetRect,
      tooltipHeight,
      tooltipWidth,
    }),
  };
}

function getPrimaryScrollTarget(): Element | Window {
  const candidates = [
    document.querySelector('.affine-page-viewport'),
    document.scrollingElement,
    document.documentElement,
    document.body,
  ].filter(
    (candidate): candidate is HTMLElement => candidate instanceof HTMLElement
  );

  const scrollTarget = candidates.find(
    candidate => candidate.scrollHeight > candidate.clientHeight + 8
  );

  return scrollTarget ?? window;
}

function scrollTutorialViewport() {
  const top = Math.max(180, Math.round(window.innerHeight * 0.45));
  const target = getPrimaryScrollTarget();

  if (target === window) {
    window.scrollBy({ top, behavior: 'smooth' });
    return;
  }

  target.scrollBy({ top, behavior: 'smooth' });
}

// --- Props ---

interface ConnectedProps {
  mode: 'connected';
}

interface StandaloneProps {
  mode: 'standalone';
  id: string;
  steps: TutorialStep[];
  onComplete: () => void;
  onSkip: () => void;
}

type TutorialRendererProps = ConnectedProps | StandaloneProps;

// --- Shared Rendering Logic ---

export function shouldShowTutorialHeader(totalSteps: number): boolean {
  return totalSteps > 1;
}

export function shouldShowTutorialNavigation(totalSteps: number): boolean {
  return totalSteps > 1;
}

export function resolveTutorialDismissAction(
  showDismissPermanentlyCheckbox: boolean,
  dismissPermanently: boolean
): 'complete' | 'defer' {
  if (showDismissPermanentlyCheckbox && !dismissPermanently) {
    return 'defer';
  }
  return 'complete';
}

function resolveTutorialTargetElements(
  step: Pick<
    TutorialStep,
    'targetResolver' | 'targetSelector' | 'targetSelectors'
  >
): Element[] {
  const resolved = step.targetResolver?.();
  const resolvedElements = resolved
    ? Array.isArray(resolved)
      ? resolved
      : [resolved]
    : [];
  const selectors =
    step.targetSelectors ?? (step.targetSelector ? [step.targetSelector] : []);
  return resolvedElements.length > 0
    ? resolvedElements
    : selectors.flatMap(selector => {
        const element = document.querySelector(selector);
        return element ? [element] : [];
      });
}

export function resolveTutorialTargetRect(
  step: Pick<
    TutorialStep,
    'targetResolver' | 'targetSelector' | 'targetSelectors'
  >
): DOMRect | null {
  const elements = resolveTutorialTargetElements(step);

  if (elements.length === 0) {
    return null;
  }

  const rects = elements.map(element => element.getBoundingClientRect());
  const left = Math.min(...rects.map(rect => rect.left));
  const top = Math.min(...rects.map(rect => rect.top));
  const right = Math.max(...rects.map(rect => rect.right));
  const bottom = Math.max(...rects.map(rect => rect.bottom));

  return new DOMRect(left, top, right - left, bottom - top);
}

interface RenderState {
  sessionId?: string;
  step: TutorialStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onDismiss?: () => void;
  /** Whether the step has a trigger (hides Next button until fallback) */
  hasTrigger: boolean;
  /** Whether the fallback "Next" button should be shown */
  showFallbackButton: boolean;
  /** Show explicit permanent-dismiss checkbox in the tutorial footer */
  showDismissPermanentlyCheckbox?: boolean;
  /** Callback to navigate the canvas viewport (only in connected mode) */
  onViewportNavigate?: (viewport: {
    x: number;
    y: number;
    zoom?: number;
  }) => void;
}

const TutorialOverlay: FC<RenderState> = ({
  sessionId,
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onDismiss,
  hasTrigger,
  showFallbackButton,
  showDismissPermanentlyCheckbox = false,
  onViewportNavigate,
}) => {
  const t = useI18n();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isPreparingStep, setIsPreparingStep] = useState(() =>
    Boolean(step.onEnter)
  );
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [tooltipSize, setTooltipSize] = useState<{
    height: number;
    width: number;
  } | null>(null);
  const [navigationIndicatorDismissed, setNavigationIndicatorDismissed] =
    useState(false);
  const [dismissPermanently, setDismissPermanently] = useState(false);

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;
  const isHint = step.variant === 'hint';
  const showHeader = shouldShowTutorialHeader(totalSteps);
  const showStepNavigation = shouldShowTutorialNavigation(totalSteps);
  const usesTouchControls = usesTouchTutorialControls(viewportWidth);
  // Legacy session/step hints control target pointers only. Card appearance
  // and motion are shared with the app tour for every tutorial context.
  const useDisclosureSurface = shouldUseDemoTourDisclosureSurface(
    sessionId,
    step.surface
  );
  const resolvedContent =
    usesTouchControls && step.mobileContent ? step.mobileContent : step.content;
  const showNavigationIndicator =
    step.showNavigationIndicator &&
    !(usesTouchControls && step.mobileControls?.length);
  const handleDismissTutorial = useCallback(() => {
    const action = resolveTutorialDismissAction(
      showDismissPermanentlyCheckbox,
      dismissPermanently
    );
    if (action === 'defer') {
      (onDismiss ?? onSkip)();
      return;
    }
    onSkip();
  }, [dismissPermanently, onDismiss, onSkip, showDismissPermanentlyCheckbox]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigate canvas viewport if step.viewport is set
  useEffect(() => {
    if (step.viewport && onViewportNavigate) {
      onViewportNavigate(step.viewport);
    }
  }, [step.viewport, onViewportNavigate]);

  useLayoutEffect(() => {
    setTargetRect(null);
    setTooltipSize(null);
    setIsPreparingStep(Boolean(step.onEnter));
    setNavigationIndicatorDismissed(false);
  }, [step]);

  useLayoutEffect(() => {
    if (isPreparingStep) {
      return;
    }
    const tooltip = document.querySelector(
      '[data-testid="tutorial-tooltip"]'
    ) as HTMLElement | null;
    if (!tooltip) {
      return;
    }

    const updateTooltipSize = () => {
      // Disclosure coachmarks animate with a scale transform. offset* reflects
      // the stable layout box; getBoundingClientRect() would feed a transient
      // animated size into the viewport clamp and can leave the final card
      // clipped near a viewport edge.
      const { height, width } = measureTutorialTooltip(tooltip);
      setTooltipSize(current => {
        if (
          current &&
          Math.abs(current.height - height) < 0.5 &&
          Math.abs(current.width - width) < 0.5
        ) {
          return current;
        }
        return { height, width };
      });
    };

    updateTooltipSize();
    const observer = new ResizeObserver(updateTooltipSize);
    observer.observe(tooltip);
    return () => observer.disconnect();
  }, [isPreparingStep, step, targetRect, viewportWidth]);

  useEffect(() => {
    if (!showNavigationIndicator || navigationIndicatorDismissed) {
      return;
    }

    let isPointerDown = false;
    let lastX = 0;
    let lastY = 0;
    let movement = 0;

    const dismiss = () => setNavigationIndicatorDismissed(true);
    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0 && event.button !== 1) return;
      isPointerDown = true;
      lastX = event.clientX;
      lastY = event.clientY;
      movement = 0;
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (!isPointerDown && event.buttons === 0) return;
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      movement += Math.hypot(dx, dy);
      if (movement >= 18) dismiss();
    };
    const handlePointerUp = () => {
      isPointerDown = false;
    };
    const handleWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) + Math.abs(event.deltaY) >= 8) dismiss();
    };

    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('pointermove', handlePointerMove, true);
    window.addEventListener('pointerup', handlePointerUp, true);
    window.addEventListener('pointercancel', handlePointerUp, true);
    window.addEventListener('wheel', handleWheel, true);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('pointermove', handlePointerMove, true);
      window.removeEventListener('pointerup', handlePointerUp, true);
      window.removeEventListener('pointercancel', handlePointerUp, true);
      window.removeEventListener('wheel', handleWheel, true);
    };
  }, [navigationIndicatorDismissed, showNavigationIndicator]);

  // Find target element and handle onEnter/onExit
  useEffect(() => {
    let mounted = true;
    let entered = false;
    const preparation = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let targetRetryId: ReturnType<typeof setTimeout> | null = null;
    let observer: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const initStep = async () => {
      // StrictMode replays effects synchronously. Do not start an imperative
      // UI sequence from an effect that has already been cleaned up.
      await Promise.resolve();
      if (!mounted) return;
      entered = true;
      if (step.onEnter) {
        try {
          await step.onEnter(preparation.signal);
        } catch (error) {
          if (preparation.signal.aborted) return;
          // A failed preparation must not leave the tour hidden and stuck:
          // report it and show the step on the UI that is actually there.
          console.error(error);
        }
      }
      if (!mounted) return;

      const delay = step.delay ?? (step.onEnter ? 100 : 0);
      if (delay > 0) {
        await new Promise(resolve => {
          timeoutId = setTimeout(resolve, delay);
        });
      }

      if (!mounted) return;
      findTarget();
      setIsPreparingStep(false);
    };

    const findTarget = () => {
      const hasTarget = Boolean(
        step.targetResolver ||
        step.targetSelector ||
        (step.targetSelectors && step.targetSelectors.length > 0)
      );
      if (!hasTarget) {
        setTargetRect(null);
        return;
      }
      const nextTargetRect = resolveTutorialTargetRect(step);
      if (nextTargetRect) {
        if (targetRetryId) {
          clearTimeout(targetRetryId);
          targetRetryId = null;
        }
        setTargetRect(nextTargetRect);
        resizeObserver?.disconnect();
        resizeObserver = new ResizeObserver(() => {
          if (!mounted) return;
          setTargetRect(resolveTutorialTargetRect(step));
        });
        for (const element of resolveTutorialTargetElements(step)) {
          resizeObserver.observe(element);
        }
        // Stop observing once found
        observer?.disconnect();
        observer = null;
      } else if (!observer) {
        // Element not in DOM yet — watch for it to appear
        setTargetRect(null);
        observer = new MutationObserver(() => {
          if (mounted) findTarget();
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }
      if (!nextTargetRect && !targetRetryId) {
        targetRetryId = setTimeout(() => {
          targetRetryId = null;
          if (mounted) findTarget();
        }, 100);
      }
    };

    initStep().catch(error => {
      if (!preparation.signal.aborted) console.error(error);
    });

    window.addEventListener('resize', findTarget);
    window.addEventListener('scroll', findTarget, true);

    return () => {
      mounted = false;
      preparation.abort();
      if (timeoutId) clearTimeout(timeoutId);
      if (targetRetryId) clearTimeout(targetRetryId);
      observer?.disconnect();
      resizeObserver?.disconnect();
      if (entered && step.onExit) {
        step.onExit();
      }
      window.removeEventListener('resize', findTarget);
      window.removeEventListener('scroll', findTarget, true);
    };
  }, [step]);

  // Keyboard: Escape to close
  // Use capture phase so the handler fires before BlockSuite's editor
  // (which swallows Escape in the bubbling phase for deselect/exit)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleDismissTutorial();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleDismissTutorial]);

  useEffect(() => {
    if (!step.blockCanvasInteraction || isPreparingStep) {
      return;
    }

    const blockedPointerIds = new Set<number>();
    let mouseGestureBlocked = false;
    const eventPathContains = (event: Event, element: Element | null) =>
      Boolean(element && event.composedPath().includes(element));
    const isWithinTooltip = (event: Event) =>
      event
        .composedPath()
        .some(
          target =>
            target instanceof HTMLElement &&
            target.dataset.testid === 'tutorial-tooltip'
        );
    const isPrimaryTargetPointer = (event: MouseEvent | PointerEvent) => {
      if (event.button !== 0) return false;
      const isWithinTargetRect =
        targetRect &&
        event.clientX >= targetRect.left &&
        event.clientX <= targetRect.right &&
        event.clientY >= targetRect.top &&
        event.clientY <= targetRect.bottom;
      return (
        isWithinTargetRect ||
        resolveTutorialTargetElements(step).some(element =>
          eventPathContains(event, element)
        )
      );
    };
    const block = (event: Event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const handleWheel = (event: WheelEvent) => {
      if (!isWithinTooltip(event)) block(event);
    };
    const handleTouchMove = (event: TouchEvent) => {
      if (!isWithinTooltip(event)) block(event);
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (isWithinTooltip(event) || isPrimaryTargetPointer(event)) {
        blockedPointerIds.delete(event.pointerId);
        return;
      }
      blockedPointerIds.add(event.pointerId);
      block(event);
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (blockedPointerIds.has(event.pointerId)) block(event);
    };
    const handlePointerEnd = (event: PointerEvent) => {
      if (!blockedPointerIds.delete(event.pointerId)) return;
      block(event);
    };
    const handleMouseDown = (event: MouseEvent) => {
      if (isWithinTooltip(event) || isPrimaryTargetPointer(event)) {
        mouseGestureBlocked = false;
        return;
      }
      mouseGestureBlocked = true;
      block(event);
    };
    const handleMouseMove = (event: MouseEvent) => {
      if (mouseGestureBlocked) block(event);
    };
    const handleMouseUp = (event: MouseEvent) => {
      if (!mouseGestureBlocked) return;
      mouseGestureBlocked = false;
      block(event);
    };

    window.addEventListener('wheel', handleWheel, {
      capture: true,
      passive: false,
    });
    window.addEventListener('touchmove', handleTouchMove, {
      capture: true,
      passive: false,
    });
    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('pointermove', handlePointerMove, true);
    window.addEventListener('pointerup', handlePointerEnd, true);
    window.addEventListener('pointercancel', handlePointerEnd, true);
    window.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('mousemove', handleMouseMove, true);
    window.addEventListener('mouseup', handleMouseUp, true);
    return () => {
      window.removeEventListener('wheel', handleWheel, true);
      window.removeEventListener('touchmove', handleTouchMove, true);
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('pointermove', handlePointerMove, true);
      window.removeEventListener('pointerup', handlePointerEnd, true);
      window.removeEventListener('pointercancel', handlePointerEnd, true);
      window.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('mousemove', handleMouseMove, true);
      window.removeEventListener('mouseup', handleMouseUp, true);
    };
  }, [isPreparingStep, step, targetRect]);

  // Tooltip positioning — respects step.position hint, always clamps within
  // viewport. Targetless coachmarks stay modal; targetless hints are fixed
  // viewport notes and leave the page interactive.
  const tooltipGeometry = targetRect
    ? resolveTutorialTooltipGeometry({
        allowNotch: useDisclosureSurface,
        centerOnTarget: step.centerOnTarget,
        forcePreferredPosition: step.forcePreferredPosition,
        preferredPosition: step.position,
        targetRect,
        tooltipHeight: tooltipSize?.height,
        tooltipWidth: tooltipSize?.width,
      })
    : undefined;
  const tooltipStyle = tooltipGeometry?.style;
  const isModal = !targetRect && !isHint;

  // Determine whether to show the Next button
  const showNextButton = !hasTrigger || showFallbackButton;
  const handleScrollIndicatorClick = useCallback(() => {
    scrollTutorialViewport();
  }, []);
  const tooltipDataProps = {
    'data-has-comparison': step.comparison?.length ? 'true' : undefined,
    'data-has-suggestions': step.suggestions?.length ? 'true' : undefined,
    'data-hint-position': step.hintPosition ?? 'bottom-left',
    'data-variant': isHint ? 'hint' : 'coachmark',
  };
  const tooltipContent: ReactNode = (
    <>
      {/* Header */}
      {showHeader ? (
        <div className={styles.header}>
          <span
            className={styles.stepIndicator}
            data-testid="tutorial-step-indicator"
            data-step={stepIndex + 1}
            data-total={totalSteps}
          >
            {t['com.streamlain.streamlain.tutorial.ui.step-of']({
              current: String(stepIndex + 1),
              total: String(totalSteps),
            })}
          </span>
          <button
            className={styles.skipButton}
            onClick={handleDismissTutorial}
            aria-label={t['com.streamlain.streamlain.tutorial.ui.skip']()}
            data-testid="tutorial-skip"
          >
            {t['com.streamlain.streamlain.tutorial.ui.skip']()}
          </button>
        </div>
      ) : null}

      {/* Content */}
      <h4 className={styles.title}>{step.title}</h4>
      <p className={styles.content}>{resolvedContent}</p>
      {step.comparison?.length ? (
        <div className={styles.comparisonList}>
          {step.comparison.map(item => (
            <section className={styles.comparisonItem} key={item.title}>
              <TutorialComparisonSceneView
                className={styles.comparisonScene}
                scene={item.scene}
              />
              <strong className={styles.comparisonTitle}>{item.title}</strong>
              <span className={styles.comparisonDescription}>
                {item.description}
              </span>
            </section>
          ))}
        </div>
      ) : null}
      {step.suggestions?.length ? (
        <div className={styles.suggestionList}>
          {step.suggestions.map(suggestion => (
            <div
              className={styles.suggestionItem}
              data-testid="tutorial-suggestion"
              data-tone={suggestion.tone}
              key={suggestion.title}
            >
              <TutorialSuggestionIllustrationView
                illustration={suggestion.illustration}
              />
              <strong className={styles.suggestionTitle}>
                {suggestion.title}
              </strong>
              <span className={styles.suggestionDescription}>
                {suggestion.description}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {step.mobileControls?.length ? (
        <div className={styles.mobileControlList}>
          {step.mobileControls.map(control => (
            <div className={styles.mobileControlItem} key={control.label}>
              <strong className={styles.mobileControlLabel}>
                {control.label}
              </strong>
              <span className={styles.mobileControlDescription}>
                {control.description}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {/* The choice applies to the buttons below, so it sits above them. */}
      <div className={styles.footer}>
        {showDismissPermanentlyCheckbox ? (
          <StreamLainCoachmarkCheckboxRow
            checked={dismissPermanently}
            label={t['do-not-show-this-again']()}
            onChange={(_, checked) => setDismissPermanently(checked)}
          />
        ) : null}

        {/* Navigation */}
        {showStepNavigation ? (
          <div className={styles.navigation}>
            <button
              className={styles.navButton}
              onClick={onPrev}
              disabled={isFirstStep}
              aria-label={t['com.streamlain.streamlain.tutorial.ui.back']()}
              data-testid="tutorial-prev"
            >
              <ArrowLeftSmallIcon width={16} height={16} />
              {t['com.streamlain.streamlain.tutorial.ui.back']()}
            </button>
            <div className={styles.navigationEnd}>
              {step.skippable && hasTrigger && (
                <button
                  className={styles.fallbackButton}
                  onClick={onNext}
                  aria-label={t[
                    'com.streamlain.streamlain.tutorial.ui.skip-step'
                  ]()}
                  data-testid="tutorial-skip-step"
                >
                  {t['com.streamlain.streamlain.tutorial.ui.skip-step']()}
                </button>
              )}
              {showNextButton && (
                <button
                  className={
                    hasTrigger ? styles.fallbackButton : styles.nextButton
                  }
                  onClick={onNext}
                  aria-label={
                    isLastStep
                      ? t['com.streamlain.streamlain.tutorial.ui.done']()
                      : t['com.streamlain.streamlain.tutorial.ui.next']()
                  }
                  data-testid="tutorial-next"
                >
                  {isLastStep
                    ? t['com.streamlain.streamlain.tutorial.ui.done']()
                    : t['com.streamlain.streamlain.tutorial.ui.next']()}
                  {!isLastStep && !hasTrigger && (
                    <ArrowRightSmallIcon width={16} height={16} />
                  )}
                </button>
              )}
            </div>
          </div>
        ) : showNextButton ? (
          <div className={styles.completionNavigation}>
            <button
              className={styles.nextButton}
              onClick={onNext}
              aria-label={t['com.streamlain.streamlain.tutorial.ui.done']()}
              data-testid="tutorial-done"
            >
              {t['com.streamlain.streamlain.tutorial.ui.done']()}
            </button>
          </div>
        ) : null}
      </div>

      {/* Tip */}
      {showStepNavigation || hasTrigger ? (
        <div className={styles.tip}>
          {hasTrigger && !showFallbackButton
            ? t['com.streamlain.streamlain.tutorial.ui.tip-action']()
            : t['com.streamlain.streamlain.tutorial.ui.tip-esc']()}
        </div>
      ) : null}
    </>
  );

  if (isPreparingStep) {
    return createPortal(
      <div
        className={styles.overlay}
        data-testid="tutorial-overlay"
        data-modal="false"
        data-preparing="true"
      />,
      document.body
    );
  }

  return createPortal(
    <div
      className={styles.overlay}
      data-testid="tutorial-overlay"
      data-modal={isModal}
      data-variant={isHint ? 'hint' : 'coachmark'}
    >
      {/* Spotlight cutout */}
      {targetRect && (
        <div
          className={styles.spotlight}
          data-animation={step.animation || 'pulse'}
          style={{
            left: targetRect.left - 8,
            top: targetRect.top - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
          }}
        />
      )}

      {step.showActionArrow && targetRect && (
        <svg
          className={styles.actionArrow}
          data-testid="tutorial-action-arrow"
          aria-hidden="true"
          width="64"
          height="36"
          viewBox="0 0 64 36"
          fill="none"
          style={{
            left: targetRect.right + 12,
            top: targetRect.top + targetRect.height / 2 - 18,
          }}
        >
          <path
            d="M56 18H8M23 4L8 18l15 14"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}

      {/* Scroll indicator */}
      {step.showScrollIndicator && (
        <button
          className={styles.scrollIndicator}
          onClick={handleScrollIndicatorClick}
          type="button"
        >
          <div className={styles.scrollIndicatorArrows}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M7 10l5 5 5-5"
                stroke="currentColor"
                opacity="0.82"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              style={{ marginTop: -12 }}
            >
              <path
                d="M7 10l5 5 5-5"
                stroke="currentColor"
                opacity="0.52"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span className={styles.scrollIndicatorLabel}>
            {t['com.streamlain.streamlain.tutorial.ui.scroll']()}
          </span>
        </button>
      )}

      {/* Canvas navigation indicator */}
      {showNavigationIndicator && (
        <div
          className={styles.navigationIndicator}
          data-dismissed={navigationIndicatorDismissed}
          aria-hidden="true"
        >
          <CanvasNavigationDemo />
        </div>
      )}

      {/* Tooltip */}
      <StreamLainCoachmarkShell
        {...tooltipDataProps}
        className={styles.tooltip}
        data-placement={tooltipGeometry?.placement}
        data-step-index={stepIndex}
        key={
          useDisclosureSurface
            ? `${sessionId}:${stepIndex}:${tooltipGeometry?.placement ?? 'modal'}`
            : sessionId
        }
        notch={tooltipGeometry?.notch ?? 'none'}
        scrollable
        style={tooltipStyle}
        surface="disclosure"
        testId="tutorial-tooltip"
      >
        {tooltipContent}
      </StreamLainCoachmarkShell>
    </div>,
    document.body
  );
};

// --- Connected Mode ---

const ConnectedTutorialRenderer: FC = () => {
  const registry = useService(TutorialRegistry);
  const activeTutorial = useLiveData(registry.activeTutorial$);

  const handleNext = useCallback(() => registry.next(), [registry]);
  const handlePrev = useCallback(() => registry.prev(), [registry]);
  const handleSkip = useCallback(() => registry.skip(), [registry]);
  const handleDismiss = useCallback(() => registry.dismiss(), [registry]);

  const handleViewportNavigate = useCallback(
    (viewport: { x: number; y: number; zoom?: number }) => {
      const editorHost = document.querySelector('editor-host');
      if (!editorHost) return;
      const std = (
        editorHost as HTMLElement & { std?: { get(id: unknown): unknown } }
      ).std;
      if (!std) return;
      const gfx = std.get(GfxControllerIdentifier) as
        | {
            viewport: {
              setViewport(
                zoom: number,
                center: [number, number],
                smooth?: boolean
              ): void;
            };
          }
        | undefined;
      if (!gfx?.viewport) return;
      gfx.viewport.setViewport(
        viewport.zoom ?? 1,
        [viewport.x, viewport.y],
        true
      );
    },
    []
  );

  const step = activeTutorial
    ? activeTutorial.session.steps[activeTutorial.step]
    : undefined;

  const { showFallbackButton } = useTutorialTrigger(
    step?.trigger,
    handleNext,
    step?.fallbackTimeoutMs
  );

  if (!activeTutorial || !step) return null;

  return (
    <TutorialOverlay
      sessionId={activeTutorial.session.id}
      step={step}
      stepIndex={activeTutorial.step}
      totalSteps={activeTutorial.session.steps.length}
      onNext={handleNext}
      onPrev={handlePrev}
      onSkip={handleSkip}
      onDismiss={handleDismiss}
      hasTrigger={!!step.trigger && step.trigger.type !== 'manual'}
      showFallbackButton={showFallbackButton}
      showDismissPermanentlyCheckbox={
        activeTutorial.session.showDismissPermanentlyCheckbox
      }
      onViewportNavigate={handleViewportNavigate}
    />
  );
};

// --- Standalone Mode ---

const StandaloneTutorialRenderer: FC<StandaloneProps> = ({
  id,
  steps,
  onComplete,
  onSkip,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [isLastStep, onComplete]);

  const handlePrev = useCallback(() => {
    setCurrentStep(prev => Math.max(0, prev - 1));
  }, []);

  if (!step) return null;

  return (
    <TutorialOverlay
      sessionId={id}
      step={step}
      stepIndex={currentStep}
      totalSteps={steps.length}
      onNext={handleNext}
      onPrev={handlePrev}
      onSkip={onSkip}
      hasTrigger={false}
      showFallbackButton={false}
    />
  );
};

// --- Public Component ---

export const TutorialRenderer: FC<TutorialRendererProps> = props => {
  if (props.mode === 'connected') {
    return <ConnectedTutorialRenderer />;
  }
  return <StandaloneTutorialRenderer {...props} />;
};
